import moment from "moment";
import { useEffect, useState } from "react";
import { Form, Button, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import SpinnerButton from "../spinner-button/spinner-button";
import { showError, showSuccess } from "../../services/toast-service";
import {
  getLeadserviceReportWebhook,
  updateLeadserviceReportWebhook,
  sendLeadserviceReportTest
} from "../../services/api-declaration";

// Slack webhook of the leadservice channel for the daily report (Mon–Fri
// 18:00). Super admins only. The stored URL is a secret and is never loaded
// back — the field only shows whether a webhook is configured.
const LeadServicePanelDailyReportWebhook = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ configured: false, updatedAt: null });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const fetchStatus = async () => {
    try {
      const resp = await getLeadserviceReportWebhook();
      setStatus(resp.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateLeadserviceReportWebhook({ webhookUrl });
      setWebhookUrl("");
      showSuccess(t("leadserviceReport.saved"));
      await fetchStatus();
    } catch (error) {
      showError(
        error?.response?.data?.message || t("leadserviceReport.saveError")
      );
    } finally {
      setIsSaving(false);
    }
  };

  // review feedback (Hamza): the backend supports unsetting with an empty
  // value, so the UI must offer it too — otherwise a stale webhook could
  // only be removed via API/DB
  const handleRemove = async () => {
    if (!window.confirm(t("leadserviceReport.removeConfirm"))) return;
    setIsRemoving(true);
    try {
      await updateLeadserviceReportWebhook({ webhookUrl: "" });
      showSuccess(t("leadserviceReport.removed"));
      await fetchStatus();
    } catch (error) {
      showError(
        error?.response?.data?.message || t("leadserviceReport.removeError")
      );
    } finally {
      setIsRemoving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    try {
      const resp = await sendLeadserviceReportTest();
      if (resp.data.data?.posted) {
        showSuccess(t("leadserviceReport.testSent"));
      } else {
        showError(t("leadserviceReport.testNotSent"));
      }
    } catch (error) {
      showError(t("leadserviceReport.testNotSent"));
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <Form.Group controlId="leadservice-report-webhook">
      <Form.Label className="d-flex align-items-center gap-2">
        {t("leadserviceReport.label")}
        <Badge bg={status.configured ? "success" : "secondary"}>
          {status.configured
            ? t("leadserviceReport.configured")
            : t("leadserviceReport.notConfigured")}
        </Badge>
        {status.configured && status.updatedAt && (
          <small className="text-muted">
            {t("leadserviceReport.updatedAt", {
              date: moment(status.updatedAt).format("DD.MM.YYYY HH:mm")
            })}
          </small>
        )}
      </Form.Label>
      <div className="d-flex align-items-center">
        <Form.Control
          type="password"
          autoComplete="off"
          className="w-50"
          placeholder="https://hooks.slack.com/… oder https://hooks.zapier.com/…"
          value={webhookUrl}
          onChange={({ target: { value } }) => setWebhookUrl(value)}
        />
        {isSaving ? (
          <SpinnerButton btnText={t("common.save")} btnStyle="ms-1" />
        ) : (
          <Button className="ms-1" disabled={!webhookUrl} onClick={handleSave}>
            {t("common.save")}
          </Button>
        )}
        {isTesting ? (
          <SpinnerButton
            btnText={t("leadserviceReport.sendTest")}
            btnStyle="ms-1"
          />
        ) : (
          <Button
            variant="outline-secondary"
            className="ms-1"
            disabled={!status.configured}
            onClick={handleTest}
          >
            {t("leadserviceReport.sendTest")}
          </Button>
        )}
        {isRemoving ? (
          <SpinnerButton
            btnText={t("leadserviceReport.remove")}
            btnStyle="ms-1"
          />
        ) : (
          <Button
            variant="outline-danger"
            className="ms-1"
            disabled={!status.configured}
            onClick={handleRemove}
          >
            {t("leadserviceReport.remove")}
          </Button>
        )}
      </div>
      <Form.Text muted>{t("leadserviceReport.hint")}</Form.Text>
    </Form.Group>
  );
};

export default LeadServicePanelDailyReportWebhook;
